import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, CalendarDays, RefreshCw } from 'lucide-react';

interface TimetableEntry { day: number; hour: number; subject_code: string; room: string | null; }
interface SubjectEntry { code: string; display_name: string | null; color: string | null; }

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

// A palette for subjects — picked hash-of-code to be stable
const COLORS = [
  'bg-blue-500/20 text-blue-200 border-blue-500/40',
  'bg-red-500/20 text-red-200 border-red-500/40',
  'bg-green-500/20 text-green-200 border-green-500/40',
  'bg-yellow-500/20 text-yellow-200 border-yellow-500/40',
  'bg-purple-500/20 text-purple-200 border-purple-500/40',
  'bg-pink-500/20 text-pink-200 border-pink-500/40',
  'bg-indigo-500/20 text-indigo-200 border-indigo-500/40',
  'bg-teal-500/20 text-teal-200 border-teal-500/40',
  'bg-orange-500/20 text-orange-200 border-orange-500/40',
  'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',
];
function colorFor(code: string): string {
  let h = 0;
  for (const c of code) h = (h * 31 + c.charCodeAt(0)) | 0;
  return COLORS[Math.abs(h) % COLORS.length];
}

const StundenplanPage: React.FC = () => {
  const [entries, setEntries] = useState<TimetableEntry[]>([]);
  const [subjects, setSubjects] = useState<SubjectEntry[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [t, s] = await Promise.all([
        api<{ entries: TimetableEntry[] }>('/api/timetable'),
        api<{ subjects: SubjectEntry[] }>('/api/subjects'),
      ]);
      setEntries(t.entries);
      setSubjects(s.subjects);
    } catch (err: any) {
      setError(err?.message || 'Konnte Stundenplan nicht laden');
    }
  };

  useEffect(() => { load(); }, []);

  const maxHour = useMemo(() => entries.reduce((m, e) => Math.max(m, e.hour), 6), [entries]);
  const daysPresent = useMemo(() => {
    const set = new Set(entries.map(e => e.day));
    const days = Array.from(set).sort();
    return days.length > 0 ? days : [0, 1, 2, 3, 4];
  }, [entries]);

  const cellFor = (day: number, hour: number) =>
    entries.find(e => e.day === day && e.hour === hour);

  const doSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api('/api/portal/sync', { method: 'POST' });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const subjectMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subjects) {
      if (s.display_name) m.set(s.code, s.display_name);
    }
    return m;
  }, [subjects]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <CalendarDays className="w-6 h-6 text-blue-400" />
          <h1 className="text-xl font-semibold">Stundenplan</h1>
        </div>
        <button onClick={doSync} disabled={syncing}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> Neu laden
        </button>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {error && <p className="mb-4 text-red-400 text-sm">{error}</p>}
        {entries.length === 0 ? (
          <p className="text-gray-400">Kein Stundenplan vorhanden. <Link to="/portal" className="text-blue-400">Schul-Portal verknüpfen</Link> und synchronisieren.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-gray-900 p-2 text-left text-xs text-gray-500 font-normal w-12">Std.</th>
                  {daysPresent.map(d => (
                    <th key={d} className="p-2 text-sm text-gray-300 font-medium text-center">{DAYS[d]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: maxHour }, (_, i) => i + 1).map(hour => (
                  <tr key={hour} className="border-t border-gray-800">
                    <td className="sticky left-0 bg-gray-900 p-2 text-sm text-gray-500 text-center">{hour}.</td>
                    {daysPresent.map(day => {
                      const c = cellFor(day, hour);
                      return (
                        <td key={`${day}-${hour}`} className="p-1 align-top">
                          {c ? (
                            <div className={`border rounded-md p-2 h-full min-h-[56px] ${colorFor(c.subject_code)}`}>
                              <div className="font-semibold text-sm">{subjectMap.get(c.subject_code) || c.subject_code}</div>
                              {c.room && <div className="text-xs opacity-80 mt-1">{c.room}</div>}
                            </div>
                          ) : (
                            <div className="border border-dashed border-gray-800 rounded-md min-h-[56px]" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {subjects.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg font-semibold mb-3">Fächer ({subjects.length})</h2>
            <div className="flex flex-wrap gap-2">
              {subjects.map(s => (
                <span key={s.code} className={`text-xs px-2 py-1 rounded border ${colorFor(s.code)}`}>
                  {s.code}{s.display_name ? ` — ${s.display_name}` : ''}
                </span>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default StundenplanPage;
